import React, { useState, useRef, useEffect } from "react";
import * as Material from "@mui/material";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import "./styles.scss";

const ImageImportTool = ({
  size,
  imageMaxWidth = 720,
  aspectRatio = 1,
  loading,
  uploading,
  src,
  onLoaded,
  onCrop = () => { },
  title,
  displayTitle = false,
  rounded = false,
  className = '',
  variant = "light"
}) => {
  const inputRef = useRef();
  const imageRef = useRef();
  const REF_cropArea = useRef();

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [preview, setPreview] = useState(src || null);
  const [crop, setCrop] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  // Sincronizar preview quando src mudar externamente
  useEffect(() => {
    if (src && src !== preview) {
      setPreview(src);
    }
  }, [src]);

  useEffect(() => {
    if (!imageRef.current || !imageSize.width || !imageSize.height) return;
    const { width: dispW, height: dispH } = imageSize;
    let cw, ch;
    const imgRatio = dispW / dispH;
    if (imgRatio > aspectRatio) {
      ch = dispH;
      cw = ch * aspectRatio;
    } else {
      cw = dispW;
      ch = cw / aspectRatio;
    }
    setCrop({
      unit: "px",
      x: (dispW - cw) / 2,
      y: (dispH - ch) / 2,
      width: Math.min(cw, dispW),
      height: Math.min(ch, dispH),
    });
  }, [selectedImage, aspectRatio, imageSize]);

  const getDisplayImageSize = (naturalWidth, naturalHeight) => {
    const maxWidth = window.innerWidth * 0.7;
    const maxHeight = window.innerHeight * 0.68;

    let width = naturalWidth;
    let height = naturalHeight;

    const ratio = width / height;

    if (width > maxWidth) {
      width = maxWidth;
      height = width / ratio;
    }

    if (height > maxHeight) {
      height = maxHeight;
      width = height * ratio;
    }

    return {
      width: Math.round(width),
      height: Math.round(height),
    };
  };

  const handleImageLoad = (imgElement) => {
    const naturalWidth = imgElement.naturalWidth;
    const naturalHeight = imgElement.naturalHeight;

    const { width: displayWidth, height: displayHeight } = getDisplayImageSize(
      naturalWidth,
      naturalHeight
    );

    setImageSize({ width: displayWidth, height: displayHeight });

    imageRef.current = imgElement;

    let cropWidth, cropHeight;

    const imageAspectRatio = displayWidth / displayHeight;

    // Garantindo que o crop tenha o aspect ratio correto
    if (imageAspectRatio > aspectRatio) {
      cropHeight = displayHeight;
      cropWidth = cropHeight * aspectRatio;
    } else {
      cropWidth = displayWidth;
      cropHeight = cropWidth / aspectRatio;
    }

    // Evita crop maior que a imagem
    cropWidth = Math.min(cropWidth, displayWidth);
    cropHeight = Math.min(cropHeight, displayHeight);

    // Ajuste do crop inicial para centralizar a imagem corretamente
    const initialCrop = {
      unit: "px",
      x: (displayWidth - cropWidth) / 2,
      y: (displayHeight - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight,
    };

    // Garantir que o `crop` seja setado corretamente na primeira renderização
    setCrop(initialCrop);
  };

  async function makeClientCrop(crop) {
    if (imageRef.current && crop.width && crop.height) {
      const croppedImageUrl = await getCroppedImg(
        imageRef.current,
        crop,
        "newFile.jpeg"
      );
      setPreview(croppedImageUrl);
    }
  }

  async function makeClientCrop(crop) {
    if (imageRef.current && crop.width && crop.height) {
      const croppedImageUrl = await getCroppedImg(
        imageRef.current,
        crop,
        "newFile.jpeg"
      );
      setPreview(croppedImageUrl);
    }
  }

  const onCropChange = (newCrop) => {
    setCrop(newCrop);
  };

  const onCropComplete = (crop) => {
    makeClientCrop(crop);
  };

  async function makeClientCrop(crop) {
    if (imageRef.current && crop.width && crop.height) {
      const croppedImageUrl = await getCroppedImg(
        imageRef.current,
        crop,
        "newFile.jpeg"
      );
      setPreview(croppedImageUrl);
    }
  }

  function getCroppedImg(image, crop, fileName) {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext("2d");

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error("Canvas is empty");
          return;
        }
        blob.name = fileName;
        resolve(window.URL.createObjectURL(blob));
      }, "image/jpeg", 1.0);
    });
  }

  const handleImageChange = async (event) => {
    const file = event.target.files[0];

    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        event.target.value = null;
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
        event.target.value = null;
        setSelectedImage(base64String);
        setPreview(base64String);
        setCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <div
        className={"upload-photo sh-1 " + (variant) + ' ' + (rounded ? "rounded-upload" : "") + ' ' + className}
        style={{
          height: size ? `${size}px` : 'auto',
          width: size ? `${size * aspectRatio}px` : '100%',
          minWidth: size ? `${size * aspectRatio}px` : '100%',
          aspectRatio: size ? undefined : aspectRatio,
        }}
        onClick={() => {
          inputRef.current?.click();
        }}
      >
        {loading && (
          <Material.Skeleton
            style={{ zIndex: 99, top: 0, left: 0 }}
            variant="rectangular"
            width={210}
            height={60}
          />
        )}

        {preview && (
          <img
            src={preview || src}
            alt="Cropped"
            key={Math.random()}
            style={{
              borderRadius: rounded ? "50%" : '9px',
            }}
          />
        )}

        <div className={" " + (!preview && "empty")}>
          <AddAPhotoIcon
            fontSize="small"
            className="add-photo-icon"
            color="black"
          />
          {
            displayTitle && (
              <span className="add-photo-title">{title}</span>
            )
          }
        </div>

        <input
          accept="image/png, image/jpeg, image/jpg"
          ref={inputRef}
          style={{ visibility: "hidden", height: 0, width: 0 }}
          type="file"
          onChange={handleImageChange}
        />
      </div>

      <Material.Modal
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
      >
        <Material.Box className="upload-photo-modal">
          <h4 className="resize-photo-title">{title}</h4>

          {selectedImage && (
            <div
              className="image-crop-preview mt-2"
              style={{
                maxWidth: "70vw",
                maxHeight: "80vh",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <ReactCrop
                ref={REF_cropArea}
                aspect={aspectRatio}
                crop={crop}
                onChange={onCropChange}
                onComplete={onCropComplete}
                ruleOfThirds
                keepSelection
                style={{
                  display: "block",
                  width: `${imageSize.width}px`,
                  height: `${imageSize.height}px`,
                  objectFit: "contain",
                }}
              >
                <img
                  ref={imageRef}
                  src={selectedImage}
                  alt="Crop"
                  style={{
                    display: "block",
                    width: `${imageSize.width}px`,
                    height: `${imageSize.height}px`,
                    objectFit: "contain",
                  }}
                  onLoad={(e) => handleImageLoad(e.currentTarget)}
                />
              </ReactCrop>
            </div>
          )}

          <Material.Button
            variant="contained"
            color="primary"
            style={{ width: "100%" }}
            className="mt-2 px-2 py-2"
            onClick={async () => {
              fetch(preview)
                .then((response) => response.blob())
                .then((blob) => {
                  const reader = new FileReader();
                  reader.onload = async (event) => {
                    const base64Image = event.target.result;
                    const file = await resizeAndConvertImage(
                      base64Image,
                      imageMaxWidth,
                      aspectRatio
                    );

                    onCrop(file);
                    setCropModalOpen(false);
                    setPreview(file);
                  };

                  reader.readAsDataURL(blob);
                })
                .catch((error) => {
                  console.error("Error fetching Blob data:", error);
                });
            }}
          >
            Salvar imagem
          </Material.Button>
        </Material.Box>
      </Material.Modal>
    </>
  );
};

export default ImageImportTool;

async function resizeAndConvertImage(file, imageMaxWidth, aspectRatio) {
  const image = new Image();

  return new Promise((resolve, reject) => {
    image.src = file;

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      let width = image.naturalWidth;
      let height = image.naturalHeight;

      if (width > imageMaxWidth) {
        height = (height * imageMaxWidth) / width;
        width = imageMaxWidth;
      }

      const currentRatio = width / height;
      const expectedRatio = aspectRatio;

      if (Math.abs(currentRatio - expectedRatio) > 0.01) {
        if (currentRatio > expectedRatio) {
          width = height * expectedRatio;
        } else {
          height = width / expectedRatio;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(image, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(blob);
        },
        "image/jpeg",
        0.95
      );
    };
  });
}
